import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LayersIcon from '@mui/icons-material/Layers';
import { Translate } from '../../../../shared/LanguageService';
import { splitAddress } from '../state/splitAddress';
import { SimultaneousGroup } from '../state/limitSimultaneousBookings';

type BookingEventContentProps = {
    timeText: string;
    title: string;
    brokerFirmName: string | null;
    addressOnly: boolean;
    simultaneous: SimultaneousGroup | null;
};

function isTruncated(element: HTMLElement) {
    return element.scrollWidth > element.clientWidth;
}

function AddressLine({ title }: { title: string }) {
    let { street, number } = splitAddress(title);

    return (
        <Typography
            variant="caption"
            component="div"
            title={title}
            sx={{ display: 'flex', minWidth: 0, gap: '0.3em' }}
        >
            <Box
                component="span"
                data-fits-check
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {street}
            </Box>
            {number && (
                <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {number}
                </Box>
            )}
        </Typography>
    );
}

function SimultaneousBadge({ group }: { group: SimultaneousGroup }) {
    const moreThanFit = Translate('customer-calendar/simultaneous/more-than-fit');

    return (
        <Tooltip title={moreThanFit} arrow>
            <Box
                component="button"
                type="button"
                data-simultaneous-badge
                aria-label={moreThanFit}
                sx={{
                    position: 'absolute',
                    top: '-12px',
                    [group.corner]: '-10px',
                    zIndex: 1,
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    flexShrink: 0,
                    px: 0.75,
                    py: 0,
                    border: 'none',
                    borderRadius: 999,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    font: 'inherit',
                    fontSize: 'caption.fontSize',
                    fontWeight: 700,
                    lineHeight: 1.6,
                    cursor: 'pointer',
                    transition: (theme) =>
                        theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
                            duration: theme.transitions.duration.shortest
                        }),
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                        transform: 'scale(1.15)',
                        boxShadow: (theme) =>
                            `0 0 0 2px ${theme.palette.background.paper}, 0 3px 8px rgba(0, 0, 0, 0.35)`
                    },
                    '&:focus-visible': {
                        outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px'
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        '&:hover': { transform: 'none' }
                    }
                }}
            >
                <LayersIcon sx={{ fontSize: '1.1em' }} />
                {`+${group.hiddenCount}`}
            </Box>
        </Tooltip>
    );
}

function BookingEventContent({
    timeText,
    title,
    brokerFirmName,
    addressOnly,
    simultaneous
}: BookingEventContentProps) {
    const container = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState<number | null>(null);
    const [tooNarrowAtWidth, setTooNarrowAtWidth] = React.useState<number | null>(null);
    const badgeCorner = simultaneous !== null ? simultaneous.corner : null;
    const showOnlyAddress = addressOnly || (width !== null && tooNarrowAtWidth === width);

    React.useLayoutEffect(() => {
        if (addressOnly || !container.current) {
            return undefined;
        }
        let element = container.current;
        setWidth(element.clientWidth);
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        let observer = new ResizeObserver(() => setWidth(element.clientWidth));
        observer.observe(element);
        return () => observer.disconnect();
    }, [addressOnly]);

    React.useLayoutEffect(() => {
        if (showOnlyAddress || width === null || !container.current) {
            return;
        }
        let lines = Array.from(
            container.current.querySelectorAll<HTMLElement>('[data-fits-check]')
        );
        if (lines.some(isTruncated)) {
            setTooNarrowAtWidth(width);
        }
    }, [showOnlyAddress, width]);

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <Box
                ref={container}
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    lineHeight: 1.3,
                    pl: badgeCorner === 'left' ? 3 : 0,
                    pr: badgeCorner === 'right' ? 3 : 0
                }}
            >
                {!showOnlyAddress && (
                    <Typography
                        variant="caption"
                        component="div"
                        noWrap
                        data-fits-check
                        sx={{ fontWeight: 600 }}
                    >
                        {timeText}
                    </Typography>
                )}
                <AddressLine title={title} />
                {!showOnlyAddress && brokerFirmName && (
                    <Typography
                        variant="caption"
                        component="div"
                        noWrap
                        data-fits-check
                        sx={{ opacity: 0.8 }}
                    >
                        {brokerFirmName}
                    </Typography>
                )}
            </Box>
            {simultaneous !== null && <SimultaneousBadge group={simultaneous} />}
        </Box>
    );
}

export default BookingEventContent;
